import {
  type ColumnDef,
  type ColumnFiltersState,
  type OnChangeFn,
  type Row,
  type SortingState,
} from "@tanstack/react-table";
import { forwardRef, type ForwardedRef, type ReactNode } from "react";
import { twMerge } from "tailwind-merge";
import { Spinner } from "../Spinner";
import { TableHeaderCell } from "./TableHeaderCell";
import { TableRow } from "./TableRow";
import { TableRowSkeleton } from "./TableRowSkeleton";
import { useTableInstance } from "./hooks/useTableInstance";
import { useTablePagination } from "./hooks/useTablePagination";
import { getSkeletonRowCount } from "./utils/getSkeletonRowCount";
import type { PaginationConfig } from "./types";

interface TableProps<TData> {
  data: TData[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  columns: ColumnDef<TData, any>[];
  isLoading?: boolean;
  isFetching?: boolean;
  rowClassName?: (row: Row<TData>) => string;
  onRowClick?: (row: Row<TData>) => void;
  pagination?: PaginationConfig;
  className?: string;
  emptyState: ReactNode;
  filteredEmptyState?: ReactNode;
  sortable?: boolean;
  manualSorting?: boolean;
  initialSorting?: SortingState;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  manualFiltering?: boolean;
  columnFilters?: ColumnFiltersState;
  onColumnFiltersChange?: OnChangeFn<ColumnFiltersState>;
  getRowTestId?: (row: Row<TData>) => string;
  testId?: string;
}

function TableInner<TData>(
  {
    data,
    columns,
    isLoading,
    isFetching,
    rowClassName,
    onRowClick,
    pagination,
    className,
    emptyState,
    filteredEmptyState,
    sortable,
    manualSorting,
    initialSorting,
    sorting,
    onSortingChange,
    manualFiltering,
    columnFilters,
    onColumnFiltersChange,
    getRowTestId,
    testId,
  }: TableProps<TData>,
  ref: ForwardedRef<HTMLDivElement>,
) {
  const table = useTableInstance({
    data,
    columns,
    enableSorting: sortable,
    manualSorting,
    initialSorting,
    sorting,
    onSortingChange,
    manualFiltering,
    columnFilters,
    onColumnFiltersChange,
  });
  const { wrap, isFetchingNextPage } = useTablePagination(
    data.length,
    pagination,
  );
  const skeletonRowCount = getSkeletonRowCount(data.length);
  const tableColumns = table.getAllColumns();
  const columnCount = tableColumns.length;
  const isRefetching = isFetching && !isFetchingNextPage && data.length > 0;
  const isInitialLoading =
    isLoading && !isFetchingNextPage && data.length === 0;
  const hasActiveFilters = table.getState().columnFilters.length > 0;

  const tableEl = (
    <table
      className={twMerge(
        "w-full min-w-max border-collapse bg-[#EDEFF2]",
        className,
      )}
    >
      <thead>
        {table.getHeaderGroups().map((headerGroup) => (
          <tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => (
              <TableHeaderCell key={header.id} header={header} />
            ))}
          </tr>
        ))}
      </thead>
      <tbody className="bg-white">
        {isInitialLoading ? (
          <tr>
            <td colSpan={columnCount}>
              <div className="flex items-center justify-center py-16">
                <Spinner className="size-7" />
              </div>
            </td>
          </tr>
        ) : (
          table
            .getRowModel()
            .rows.map((row) => (
              <TableRow
                key={row.id}
                row={row}
                rowClassName={rowClassName}
                onRowClick={onRowClick}
                loading={isRefetching}
                testId={getRowTestId?.(row)}
              />
            ))
        )}
        {!isLoading && !isFetchingNextPage && data.length === 0 && (
          <tr>
            <td colSpan={columnCount}>
              {hasActiveFilters && filteredEmptyState
                ? filteredEmptyState
                : emptyState}
            </td>
          </tr>
        )}
        {isFetchingNextPage &&
          Array.from({ length: skeletonRowCount }).map((_, i) => (
            <TableRowSkeleton key={`next-${i}`} columns={tableColumns} />
          ))}
      </tbody>
    </table>
  );

  return (
    <div
      data-testid={testId}
      className="relative flex max-h-full flex-col overflow-hidden rounded-lg border"
    >
      <div ref={ref} className="min-h-0 overflow-auto">
        {wrap(tableEl)}
      </div>
    </div>
  );
}

export const Table = forwardRef(TableInner) as <TData>(
  props: TableProps<TData> & { ref?: ForwardedRef<HTMLDivElement> },
) => ReturnType<typeof TableInner>;
